"use client";

import { useSyncExternalStore } from "react";

const QUERY = "(prefers-reduced-motion: reduce)";

function subscribe(callback: () => void) {
  const mql = window.matchMedia(QUERY);
  mql.addEventListener("change", callback);
  return () => mql.removeEventListener("change", callback);
}

function getSnapshot() {
  return window.matchMedia(QUERY).matches;
}

function getServerSnapshot() {
  // SSR has no media query — render the more-animated path and downgrade
  // on the client if the user has the OS-level setting on.
  return false;
}

/**
 * Read `(prefers-reduced-motion: reduce)` reactively **and synchronously**.
 *
 * Unlike Motion's `useReducedMotion()`, this resolves to the correct value
 * on the first client render (via `useSyncExternalStore`), with no
 * null-then-true frame. That matters for the imperative scroll/loop hooks
 * (`use-logo-scroll-animation`, `use-marquee-loop`): they write transforms
 * and sizes straight to the DOM, so a single frame of "not reduced yet"
 * would start an animation and leave a stray inline style that defeats the
 * static CSS fallback. Declarative Motion components don't need this — they
 * just use Motion's `useReducedMotion()` (or the global `MotionConfig`).
 */
export function usePrefersReducedMotion(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
